import {
  Badge,
  Button,
  Col,
  Dropdown,
  Form,
  Input,
  MenuProps,
  Row,
  Select,
} from "antd";
import dayjs from "dayjs";
import {
  DeleteOutlined,
  EllipsisOutlined,
  ExclamationCircleFilled,
  HomeOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import SinglePageContainer from "../../../Common/SinglePageContainer";
import FieldTechniciansForm, {
  FieldTechnicianFormValues,
  FieldTechniciansFormProps,
} from "../Form";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../../../../context/breadcrumbs";
import {
  FieldTechnicianUpdateParams,
  useFieldTechnicianDelete,
  useFieldTechnicianGetById,
  useFieldTechnicianUpdate,
} from "../../../../hooks/api/fieldTechnicians";
import { useLocation, useNavigate, useParams } from "react-router";
import { UserStatusType } from "../../../../store/auth";
import { useMessageError, useMessageSuccess } from "../../../../hooks/common";
import { useSimpleModal } from "../../../Common/Modal/Simple";
import { UserStatusDisplay } from "../../../../enums/user";
import { enumToOptionsArray } from "../../../../utils";
import { getUserStatusColor } from "..";
import Loading from "../../../Common/Loading";

const initialState: FieldTechnicianFormValues = {
  firstName: "",
  lastName: "",
  email: "",
  status: "pending" as UserStatusType,
};

export default function FieldTechniciansEditContent(): React.ReactNode | null {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const location = useLocation();
  const fromPage = location.state?.fromPage || "1";
  const [form] = Form.useForm<FieldTechnicianFormValues>();
  const [initialValues, setInitialValues] =
    useState<FieldTechnicianFormValues>(initialState);
  const [readOnlyData, setReadOnlyData] = useState<
    FieldTechniciansFormProps["readOnlyValues"]
  >({
    phone: "",
    COPSMatch: "",
    lastLoginAt: "",
  });
  const { open, contextHolder } = useSimpleModal();
  const fieldTechnicianById = useFieldTechnicianGetById(id);
  const fieldTechnicianUpdate = useFieldTechnicianUpdate(id);
  const fieldTechnicianDelete = useFieldTechnicianDelete(id);

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: "Field Technicians", href: "/field-technicians" },
      { title: t("common.edit") },
    ]);
  }, [i18n.language, t]);

  useEffect(() => {
    if (
      fieldTechnicianById.data &&
      !fieldTechnicianById.loading &&
      !fieldTechnicianById.error
    ) {
      const data = fieldTechnicianById.data;

      setInitialValues({
        firstName: data.user.firstName,
        lastName: data.user.lastName,
        email: data.user.email,
        status: data.user.status,
      });

      form.setFieldsValue({
        firstName: data.user.firstName,
        lastName: data.user.lastName,
        email: data.user.email,
        status: data.user.status,
      });

      setReadOnlyData({
        phone: data.user.phone,
        COPSMatch: data.COPSMatch,
        lastLoginAt: data.user.lastLoginAt
          ? dayjs(data.user.lastLoginAt).format("YYYY-MM-DD HH:mm:ss")
          : "-",
      });
    }
  }, [fieldTechnicianById.data]);
  useEffect(() => {
    if (fieldTechnicianUpdate.data) {
      navigate(`/field-technicians?current=${fromPage}`);
    }
  }, [fieldTechnicianUpdate.data, fromPage, navigate]);

  useEffect(() => {
    if (
      !fieldTechnicianDelete.error &&
      !fieldTechnicianDelete.loading &&
      fieldTechnicianDelete.data
    ) {
      navigate(`/field-technicians?current=${fromPage}`);
    }
  }, [
    fieldTechnicianDelete.error,
    fieldTechnicianDelete.loading,
    fieldTechnicianDelete.data,
    fromPage,
    navigate,
  ]);

  const items: MenuProps["items"] = [
    {
      key: "delete",
      type: "item",
      label: <span style={{ color: "red" }}>{t("common.delete")}</span>,
      onClick: () => {
        open({
          icon: <ExclamationCircleFilled />,
          title: "Delete Field Technician",
          content: (
            <span>
              Are you sure you want to delete{" "}
              <b>
                {initialValues.firstName || "-"}{" "}
                {initialValues?.lastName || "-"}
              </b>
              ?
            </span>
          ),
          cancelText: t("common.cancel"),
          okText: t("common.delete"),
          okButtonProps: { danger: true },
          onOk: () => fieldTechnicianDelete.fetch(),
        });
      },
    },
  ];

  const handleSubmit = async (values: FieldTechnicianFormValues) => {
    const payload: FieldTechnicianUpdateParams = {
      user: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        status: values.status,
      },
    };

    fieldTechnicianUpdate.fetch(payload);
  };

  useMessageSuccess([fieldTechnicianUpdate], "Updates saved.");
  useMessageError([fieldTechnicianUpdate]);

  return (
    <SinglePageContainer
      title="Edit Field Technician"
      extra={
        <>
          <Button type="primary" onClick={() => form.submit()}>
            {t("common.save")}
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              open({
                icon: <ExclamationCircleFilled />,
                title: "Delete Field Technician",
                content: (
                  <span>
                    Are you sure you want to delete{" "}
                    <b>
                      {initialValues.firstName || "-"}{" "}
                      {initialValues?.lastName || "-"}
                    </b>
                    ?
                  </span>
                ),
                cancelText: t("common.cancel"),
                okText: t("common.delete"),
                okButtonProps: { danger: true },
                onOk: () => fieldTechnicianDelete.fetch(),
              });
            }}
          />
        </>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={handleSubmit}
      >
        <FieldTechniciansForm readOnlyValues={readOnlyData} />
      </Form>
      {contextHolder}
      {fieldTechnicianById.loading || fieldTechnicianUpdate.loading ? (
        <Loading absolute />
      ) : null}
    </SinglePageContainer>
  );
}
